/**
 * AdoraCare - the doctor's TREATMENT JOURNEY header
 * (MULTI-VISIT-TREATMENT-JOURNEY-V1, board row V6-P4; recomposed by
 * DOCTOR-COCKPIT-VISUAL-CONFORM-V1 round 3 -- the founder architecture
 * ruling on PR #529).
 *
 * ROUND 3: this component is now the COURSE CARD'S HEAD BLOCK (the
 * candidate-b journey grammar -- title + status chip + "est. Rs n" + the
 * progress bar + the four-segment meta line with "opened <date>"), rendered
 * INSIDE TreatmentAdvisedSection via the journeySlot. The per-visit
 * chronicle is RETIRED here (founder correction 5: the rail's Earlier
 * Visits owns visit history); its cross-sheet "Completed this visit" act
 * moved onto the course-wide LEDGER rows (same completePlanItem write --
 * the act's door moved, the write path did not).
 *
 * THE JOURNEY READ IS LIFTED to the page (one fetch, several renders: this
 * head, the course ledger rows, the completion gating). This component is
 * presentation + the start-journey act only.
 *
 * LAWS surfaced verbatim:
 *   - An item is NEVER silently completed -- only the explicit act.
 *   - Pre-apply (FILE-ONLY 20260717_1) the head renders the honest
 *     "completion evidence unavailable" state -- never "0 completed".
 *   - The DOCTOR wire carries NO collected/outstanding money (structurally
 *     absent); the head shows clinical progress only.
 */

import { useCallback, useState } from 'react';

import {
  createTreatmentCourseForSheet,
  type CaseSheetClinicalView,
  type CourseJourneyResponse,
} from '@/dashboard/api';
import { AcBadge, AcButton, SectionShell } from '@/dashboard/ui';
import { rupeesLabel } from '@/dashboard/doctor/doctor-consult-view';
import type { AcceptedStateTruth } from '@/dashboard/doctor/consult-accepted-truth';
import { formatIstShortDate } from '@/lib/datetime-ist';

const COMPLETION_UNAVAILABLE_COPY =
  'Completion evidence is not available yet (migration pending founder apply).';

/** Short IST date for the meta line ("12 Jun"); null-safe. DOCTOR-CONSULT-
 *  DEMO-POLISH-V1 (item 2, ONE CLOCK): delegates to the page's ONE
 *  org-timezone short-date formatter (formatIstShortDate) instead of a
 *  second, locally-duplicated Intl conversion, so the journey's "opened"
 *  date and the visit date elsewhere on this screen can never drift apart. */
function shortIstDate(iso: string | null | undefined): string | null {
  if (typeof iso !== 'string' || iso === '') return null;
  const formatted = formatIstShortDate(iso);
  return formatted === '' ? null : formatted;
}

export type JourneyLoadState = 'none' | 'loading' | 'error' | 'ready';

export function TreatmentJourneyPanel({
  sheet,
  journey,
  journeyState,
  liveProgress = null,
  onSheetRefresh,
  onReload,
  compact = false,
}: {
  /** The CURRENT visit's doctor view (the completing visit context). */
  sheet: CaseSheetClinicalView;
  /** The page-lifted journey read (null until ready / when no course). */
  journey: CourseJourneyResponse | null;
  journeyState: JourneyLoadState;
  /** DOCTOR-CONSULT-TRUTH-AND-GATE-CORRECTIVE-V1 (C2): the LIVE course
   *  truth from THE accepted-state truth owner (earlier-visit ledger rows +
   *  this sheet's live plan items -- consult-accepted-truth.ts). Provided by
   *  the consult page so the header counters can never lag the line chips
   *  behind a stale journey wire; absent = older fixtures render the wire's
   *  own snapshot byte-identically. */
  liveProgress?: AcceptedStateTruth | null;
  /** Called with the refreshed doctor view after the start act. */
  onSheetRefresh: (view: CaseSheetClinicalView) => void;
  /** Retry door for a failed journey read (the page refetches). */
  onReload: () => void;
  /** Embedded in the approved C Visit plan card; no second card heading. */
  compact?: boolean;
}): JSX.Element | null {
  const courseId = sheet.treatmentCourseId;
  const [busyStart, setBusyStart] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const onStartJourney = useCallback(() => {
    setBusyStart(true);
    setFeedback(null);
    createTreatmentCourseForSheet(sheet.id, {})
      .then((result) => {
        onSheetRefresh(result.caseSheet);
        setFeedback('Treatment journey started for this plan.');
      })
      .catch((err: unknown) =>
        setFeedback(
          err instanceof Error ? err.message : 'Could not start the journey.',
        ),
      )
      .finally(() => setBusyStart(false));
  }, [sheet.id, onSheetRefresh]);

  // No course yet: offer the start act on an open (draft) visit only -- a
  // finalized visit's course link is frozen with the rest of the sheet.
  if (courseId === null) {
    if (sheet.status !== 'draft') return null;
    return (
      <SectionShell
        embedded={compact}
        eyebrow="Multi-visit care"
        title="Treatment journey"
        testId="consult-journey-start"
      >
        <AcButton
          size="sm"
          variant="secondary"
          disabled={busyStart}
          onClick={onStartJourney}
          title="Treatment planned across several visits? Start a journey so later visits, payments, and progress stay on ONE plan."
          data-testid="consult-journey-start-btn"
        >
          {busyStart ? 'Starting...' : 'Start treatment journey'}
        </AcButton>
        {feedback !== null ? (
          <p className="cockpit-plan__legend" role="status">
            {feedback}
          </p>
        ) : null}
      </SectionShell>
    );
  }

  const openedShort = shortIstDate(journey?.openedAt ?? null);

  // C2 (TRUTH-AND-GATE corrective): counts resolve LIVE-first. The wire's
  // snapshot is the fallback for older fixtures only -- on the real page the
  // truth-owner composition wins, so "0 accepted" can never sit beside an
  // "Accepted - planned" line chip (the 2026-08-14 walked drift).
  const counts =
    journey === null
      ? null
      : {
          itemCount: liveProgress?.itemCount ?? journey.progress.itemCount,
          acceptedCount:
            liveProgress?.acceptedCount ?? journey.progress.acceptedCount,
          completedCount:
            liveProgress?.completedCount ?? journey.progress.completedCount,
          remainingPlannedCount:
            liveProgress?.remainingPlannedCount ??
            journey.progress.remainingPlannedCount,
          undecidedCount:
            liveProgress?.undecidedCount ??
            journey.decisionCounts.undecidedCount,
        };

  return (
    <div className="consult-journey" data-testid="consult-journey">
      {/* The reference's journey-title row: name (16px/700) + Active chip +
          the mono "est. Rs n" at the right edge. */}
      <div className="consult-journey__jt">
        <b className="consult-journey__name">
          {journey !== null && journey.courseName !== null
            ? journey.courseName
            : 'Treatment journey'}
        </b>
        {journey !== null ? (
          <AcBadge tone="brand">
            {journey.courseStatus === 'active' || journey.courseStatus === 'planned'
              ? 'Active'
              : journey.courseStatus}
          </AcBadge>
        ) : null}
        <span className="consult-journey__grow" />
        {journey !== null && journey.estimatedTotalValueInr !== null ? (
          <span
            className="consult-journey__est"
            data-testid="consult-journey-est"
          >
            {`est. ${rupeesLabel(journey.estimatedTotalValueInr)}`}
          </span>
        ) : null}
      </div>
      {journey !== null &&
      counts !== null &&
      journey.completionAvailability === 'available' &&
      counts.itemCount > 0 ? (
        <div
          className="consult-journey__pbar"
          data-testid="consult-journey-progress"
          role="img"
          aria-label={`${counts.completedCount} of ${counts.acceptedCount} accepted items completed`}
        >
          <span
            className="consult-journey__pbar-done"
            style={{
              width: `${(counts.completedCount / counts.itemCount) * 100}%`,
            }}
          />
          <span
            className="consult-journey__pbar-plan"
            style={{
              width: `${(counts.remainingPlannedCount / counts.itemCount) * 100}%`,
            }}
          />
        </div>
      ) : null}
      {/* The reference's four-segment meta line: mono-bold numbers +
          "opened <date>" (the round-3 openedAt widening). Pre-apply the
          honest completion-unavailable sentence replaces the counts. */}
      {journey !== null && counts !== null ? (
        journey.completionAvailability === 'available' ? (
          <div className="consult-journey__pmeta" data-testid="consult-journey-meta">
            <span>
              <b>{counts.completedCount}</b> completed
            </span>
            <span>
              <b>{Math.max(0, counts.remainingPlannedCount)}</b>{' '}
              accepted, planned
            </span>
            <span>
              <b>{counts.undecidedCount}</b> advised, undecided
            </span>
            {openedShort !== null ? (
              <span>
                opened <b>{openedShort}</b>
              </span>
            ) : null}
          </div>
        ) : (
          <p className="cockpit-plan__legend">{COMPLETION_UNAVAILABLE_COPY}</p>
        )
      ) : null}
      {journeyState === 'loading' && journey === null ? (
        <p className="day-board__loading" role="status">
          Loading the journey...
        </p>
      ) : null}
      {journeyState === 'error' ? (
        <p role="alert" data-testid="consult-journey-error">
          Treatment journey unavailable.{' '}
          <AcButton size="sm" variant="secondary" onClick={onReload}>
            Retry
          </AcButton>
        </p>
      ) : null}
      {feedback !== null ? (
        <p className="cockpit-plan__legend" role="status">
          {feedback}
        </p>
      ) : null}
    </div>
  );
}
